/**
 * Centralized Google Analytics, Tag Manager, and Google Ads Tracking Service
 * DSMR Solutions
 */

const GOOGLE_ADS_LEAD_CONVERSION_ID = 'AW-17378151442/nHWRCPrV6O0bEJKYxt5A';
const DEFAULT_PHONE_NUMBER = '+918700319769';

// Prevent duplicate conversion dispatches within a short timeframe
const recentConversions = new Set();

/**
 * Safely access the global gtag function
 */
function getGtag() {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    return window.gtag;
  }
  return null;
}

/**
 * Fire telephone / contact click conversion
 * Supports immediate calls or delayed link navigation with a 2000ms safety timeout
 * 
 * @param {Event} [event] - Optional click event
 * @param {string} [destinationUrl] - Optional tel: or external link
 */
export function trackCallConversion(event, destinationUrl) {
  const gtag = getGtag();

  // If no gtag is available (e.g. ad-blocker or script failed), fail gracefully
  if (!gtag) {
    if (destinationUrl && (!event || !event.defaultPrevented)) {
      if (typeof window !== 'undefined') {
        window.location.href = destinationUrl;
      }
    }
    return;
  }

  // If destinationUrl is supplied and event was passed, ensure graceful navigation after callback or timeout
  if (destinationUrl && event) {
    let navigated = false;
    const navigate = () => {
      if (!navigated) {
        navigated = true;
        if (typeof window !== 'undefined') {
          window.location.href = destinationUrl;
        }
      }
    };

    // Prevent default browser click while waiting for callback or 2s timeout
    try {
      event.preventDefault();
    } catch (_) {}

    const timer = setTimeout(navigate, 2000);

    try {
      gtag('event', 'conversion_event_contact', {
        event_callback: () => {
          clearTimeout(timer);
          navigate();
        },
        event_timeout: 2000,
      });
    } catch (err) {
      clearTimeout(timer);
      navigate();
    }
  } else {
    // Standard event fire without intercepting browser navigation
    try {
      gtag('event', 'conversion_event_contact');
    } catch (err) {
      console.warn('Call conversion dispatch notice:', err);
    }
  }
}

/**
 * Fire Google Ads lead conversion ONLY after confirmed successful lead submission
 * Destination: AW-17378151442/nHWRCPrV6O0bEJKYxt5A
 * Value: 1.0 INR
 * 
 * @param {Object} [details] - Optional lead details for deduplication
 */
export function trackLeadConversion(details = {}) {
  const dedupeKey = details.id || details.mobileNumber || details.email || `${Date.now()}`;
  if (recentConversions.has(dedupeKey)) {
    return;
  }
  recentConversions.add(dedupeKey);
  setTimeout(() => recentConversions.delete(dedupeKey), 10000);

  const gtag = getGtag();
  if (!gtag) {
    return;
  }

  try {
    gtag('event', 'conversion', {
      send_to: GOOGLE_ADS_LEAD_CONVERSION_ID,
      value: 1.0,
      currency: 'INR',
    });
  } catch (err) {
    console.warn('Lead conversion dispatch notice:', err);
  }
}

/**
 * Initialize dynamic phone number updater for Google Ads phone call conversion
 * Callback from gtag('config', 'AW-17378151442', { phone_conversion_number: '+918700319769', phone_conversion_callback: ... })
 */
export function initPhoneConversionUpdater() {
  if (typeof window === 'undefined') return;

  window.__dsmrCurrentTrackedPhone = window.__dsmrCurrentTrackedPhone || {
    formatted: '+91 8700319769',
    unformatted: DEFAULT_PHONE_NUMBER,
  };

  window.__dsmrUpdatePhoneNumbers = function (formatted, unformatted) {
    if (!formatted && !unformatted) return;

    window.__dsmrCurrentTrackedPhone = {
      formatted: formatted || unformatted,
      unformatted: unformatted || formatted,
    };

    const targetNumber = unformatted || formatted;
    const cleanTargetTel = targetNumber.replace(/[^\d+]/g, '');

    // Select all telephone links in the DOM
    const links = document.querySelectorAll('a[href^="tel:"], [data-dsmr-phone]');
    links.forEach((link) => {
      // Update href
      link.setAttribute('href', `tel:${cleanTargetTel}`);

      // Update visible phone text if present in data-phone-text or matching standard number
      const phoneTextElements = link.querySelectorAll('[data-dsmr-phone-text], .phone-number');
      if (phoneTextElements.length > 0) {
        phoneTextElements.forEach((el) => {
          el.textContent = formatted;
        });
      } else if (link.textContent && (link.textContent.includes('8700319769') || link.textContent.includes('87003 19769'))) {
        // Find text nodes containing the phone number and replace text without removing icons
        Array.from(link.childNodes).forEach((child) => {
          if (child.nodeType === Node.TEXT_NODE && child.textContent.includes('8700319769')) {
            child.textContent = child.textContent.replace(/(\+?91[\s-]?)?87003\s?19769/, formatted);
          }
        });
      }
    });
  };
}

export default {
  trackCallConversion,
  trackLeadConversion,
  initPhoneConversionUpdater,
};
